import { useState } from "react";
import { Modal } from "../ui/Modal";
import { Button } from "../ui/Button";
import { AlertTriangle } from "lucide-react";
import { userService } from "../../services/userService";

interface DeleteUserDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  user: {
    _id: string;
    firstName?: string;
    lastName?: string;
    email: string;
    role: string;
  } | null;
}

export function DeleteUserDialog({
  isOpen,
  onClose,
  onSuccess,
  user,
}: DeleteUserDialogProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleDelete = async () => {
    if (!user) return;

    setIsLoading(true);
    setError(null);

    try {
      await userService.deleteUser(user._id);
      onSuccess();
      onClose();
    } catch (err: any) {
      console.error("Delete user error:", err);
      setError(
        err.response?.data?.message ||
          err.message ||
          "Failed to delete user. Please try again."
      );
    } finally {
      setIsLoading(false);
    }
  };

  if (!user) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Delete User">
      <div className="space-y-4">
        <div className="flex items-center gap-3 p-4 bg-red-50 text-red-700 rounded-lg border border-red-100">
          <AlertTriangle size={24} className="shrink-0" />
          <p className="text-sm font-medium">
            Are you sure you want to delete this {user.role} account? This
            action cannot be undone.
          </p>
        </div>

        <div className="p-4 bg-gray-50 rounded-lg border border-gray-100">
          <p className="text-sm text-gray-500 mb-1">User to delete:</p>
          <p className="font-semibold text-gray-900">
            {user.firstName} {user.lastName}
          </p>
          <p className="text-sm text-gray-600">{user.email}</p>
        </div>

        {error && (
          <div className="p-3 text-sm text-red-600 bg-red-50 rounded-lg border border-red-100">
            {error}
          </div>
        )}

        <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
          <Button variant="outline" onClick={onClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleDelete}
            disabled={isLoading}
            className="bg-red-600 hover:bg-red-700 border-red-600 hover:border-red-700 text-white"
          >
            {isLoading ? "Deleting..." : "Delete User"}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
